import { Auth0Provider } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'

/**
 * AuthProvider
 *
 * Wraps the app with Auth0Provider and configures:
 * - Refresh tokens (offline_access)
 * - LocalStorage caching
 * - Cookie-based transaction storage (prevents "missing_transaction" loops)
 * - A redirect callback that removes ?code&state and routes to /chat
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export default function AuthProvider({ children }) {
  const navigate = useNavigate()

  /**
   * Called by @auth0/auth0-react after it processes the redirect callback.
   *
   * IMPORTANT:
   * We immediately remove the query params (?code&state) to avoid the router
   * navigating away and losing the callback state.
   *
   * @param {Record<string, any>} [appState]
   */
  const onRedirectCallback = (appState) => {
    const returnTo = appState?.returnTo || '/chat'

    window.history.replaceState({}, document.title, returnTo)

    localStorage.setItem('justLoggedIn', 'true')
    navigate(returnTo, { replace: true })
  }

  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
  const audience = import.meta.env.VITE_AUTH0_API_AUDIENCE
  const domain = import.meta.env.VITE_AUTH0_DOMAIN
  const organization = import.meta.env.VITE_AUTH0_ORG
  const useDev = import.meta.env.VITE_APP_DISABLE_AUTH0 === 'true'

  if (!domain || !clientId) {
    console.error('Auth0 configuration missing. Please check your .env file.')
    console.error('Required environment variables:')
    console.error('- VITE_AUTH0_DOMAIN')
    console.error('- VITE_AUTH0_CLIENT_ID')
    throw new Error('Auth0 domain and client ID must be set in .env file')
  }

  const DEV_ENVIRONMENTS = ['localhost', '127.0.0.1', '0.0.0.0']
  const { hostname } = window.location
  const isDev = DEV_ENVIRONMENTS.includes(hostname)

  /**
   *
   * Make sure Auth0 dashboard "Allowed Callback URLs" includes:
   * - http://localhost:3000/chat
   * - https://pilot.edelman-ai.app/chat
   */
  const redirectUri = isDev
    ? `${window.location.origin}/chat`
    : 'https://pilot.edelman-ai.app/chat'

  if (
    !domain.includes('.auth0.com') &&
    !domain.includes('.us.auth0.com') &&
    !domain.includes('.eu.auth0.com') &&
    !domain.includes('.au.auth0.com')
  ) {
    console.warn(
      'Auth0 domain format might be incorrect. Expected format: your-domain.auth0.com'
    )
  }

  /** @param {{ children: React.ReactNode }} props */
  const DevProvider = ({ children }) => <>{children}</>

  const auth0Props = useDev
    ? {}
    : {
        clientId,
        domain,
        cacheLocation: 'localstorage',
        // useRefreshTokens: true,

        // useCookiesForTransactions: true,

        authorizationParams: {
          audience,
          organization,
          redirect_uri: redirectUri,
          // scope: 'openid profile email offline_access',
        },

        onRedirectCallback,
      }

  if (useDev) {
    console.error('AUTH0 DISABLED')
  }

  const ProviderComponent = useDev ? DevProvider : Auth0Provider
  return <ProviderComponent {...auth0Props}>{children}</ProviderComponent>
}
